import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBolt,
  FaBroadcastTower,
  FaBullhorn,
  FaCalendarAlt,
  FaCalendarWeek,
  FaCoins,
  FaCompass,
  FaCrown,
  FaFire,
  FaGem,
  FaGift,
  FaHeadphones,
  FaHeart,
  FaLightbulb,
  FaListUl,
  FaLock,
  FaMicrophone,
  FaMusic,
  FaPlay,
  FaRecordVinyl,
  FaRocket,
  FaStar,
  FaTheaterMasks,
  FaThumbsUp,
  FaTicketAlt,
  FaTrophy,
  FaUsers,
  FaVolumeUp,
} from 'react-icons/fa';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import {
  ACHIEVEMENTS_ENDPOINT,
  ACHIEVEMENTS_STATUS,
  earnableMetrics,
  formatProgress,
  groupAchievements,
  hasAchievementActivity,
  localizeAchievements,
  normalizeAchievementsResponse,
  pickNewAchievementKeys,
  readSeenAchievementKeys,
  summarizeAchievements,
  writeSeenAchievementKeys,
} from '../_helpers/listenerAchievements.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { englishT, languageOf } from '../locales/index.js';
import { richMessage } from '../_helpers/richMessage.jsx';
import config from '../config.js';
import styles from '../styles/ListenerAchievementsPage.module.css';

/**
 * The glyph for each icon name a badge can carry: exactly
 * `ACHIEVEMENT_ICON_NAMES` (the backend's vocabulary plus the client's own
 * names; the tests hold the two together). A name not here renders as the
 * trophy.
 */
export const ICONS = Object.freeze({
  headphones: FaHeadphones,
  radio: FaBroadcastTower,
  vinyl: FaRecordVinyl,
  flame: FaFire,
  'calendar-week': FaCalendarWeek,
  'calendar-month': FaCalendarAlt,
  heart: FaHeart,
  star: FaStar,
  crowd: FaUsers,
  'music-note': FaMusic,
  playlist: FaListUl,
  microphone: FaMicrophone,
  speaker: FaVolumeUp,
  applause: FaThumbsUp,
  stage: FaTheaterMasks,
  coin: FaCoins,
  gift: FaGift,
  gem: FaGem,
  trophy: FaTrophy,
  ticket: FaTicketAlt,
  spotlight: FaLightbulb,
  crown: FaCrown,
  rocket: FaRocket,
  lightning: FaBolt,
  megaphone: FaBullhorn,
  music: FaMusic,
  users: FaUsers,
  compass: FaCompass,
  play: FaPlay,
});

/** The filter tabs, each named by its catalog key; `emptyKey` is the message when it shows no badge. */
const FILTERS = [
  { id: 'all', labelKey: 'achievements.filters.all', emptyKey: 'achievements.empty.all' },
  { id: 'unlocked', labelKey: 'achievements.filters.unlocked', emptyKey: 'achievements.empty.unlocked' },
  { id: 'locked', labelKey: 'achievements.filters.locked', emptyKey: 'achievements.empty.locked' },
];

/**
 * The message for a tab that shows no badge. With no catalogue at all there
 * is nothing to have unlocked or finished, so every tab says so rather than
 * celebrating. An empty locked tab otherwise means the set is finished — a
 * claim a withheld secret badge (ENG-411) makes untrue, so while the response
 * carried one the tab reports only what it can see: nothing locked here.
 */
const emptyMessageKey = (filter, summary) => {
  if (summary.total === 0) return 'achievements.empty.all';
  if (filter === 'locked' && !summary.allCollected) return 'achievements.empty.lockedNone';
  return FILTERS.find((tab) => tab.id === filter).emptyKey;
};

/**
 * The backend metrics a listener with nothing yet can be told to act on, in
 * the order the sentence lists them. A metric whose source is not enabled on
 * this deployment is left out, so the page never urges something that cannot
 * earn a badge here; a metric this build does not know gets the generic
 * sentence.
 */
const INTRO_METRICS = ['listen', 'follow', 'request'];

/**
 * One whole sentence per combination of earnable metrics: a list is not built
 * from fragments, since the joining words differ by language.
 */
const INTRO_KEYS = {
  listen: 'achievements.intro.listen',
  follow: 'achievements.intro.follow',
  request: 'achievements.intro.request',
  'listen,follow': 'achievements.intro.listenFollow',
  'listen,request': 'achievements.intro.listenRequest',
  'follow,request': 'achievements.intro.followRequest',
  'listen,follow,request': 'achievements.intro.listenFollowRequest',
};

const GENERIC_INTRO_KEY = 'achievements.intro.generic';

/**
 * The nudge for a listener with no activity, naming only the actions that
 * can still earn a badge here, or `null` when nothing can (every badge is
 * unlocked or unavailable, or there are none). In the locale of `t`; English
 * without one.
 */
export const introText = (metrics, t = englishT) => {
  if (metrics.size === 0) return null;
  const present = INTRO_METRICS.filter((metric) => metrics.has(metric)).join(',');
  return t(INTRO_KEYS[present] ?? GENERIC_INTRO_KEY);
};

/** The English nudge when every backend metric can be earned. */
export const INTRO_TEXT = introText(new Set(INTRO_METRICS));

/** Catalog key of the notice for each overall response status other than `ready`. */
const STATUS_KEYS = {
  [ACHIEVEMENTS_STATUS.PROCESSING]: 'achievements.status.processing',
  [ACHIEVEMENTS_STATUS.UNAVAILABLE]: 'achievements.status.unavailable',
};

/* English copy of the page's fixed messages, for tests and callers without a locale. */

/** Shown for the overall response status when it is not `ready`. */
export const STATUS_TEXT = {
  [ACHIEVEMENTS_STATUS.PROCESSING]: englishT(STATUS_KEYS[ACHIEVEMENTS_STATUS.PROCESSING]),
  [ACHIEVEMENTS_STATUS.UNAVAILABLE]: englishT(STATUS_KEYS[ACHIEVEMENTS_STATUS.UNAVAILABLE]),
};

/** Shown on a locked badge whose source is not enabled on this deployment. */
export const UNAVAILABLE_BADGE_TEXT = englishT('common.status.notAvailableYet');

export const ERROR_TEXT = englishT('achievements.errors.loadFailed');

/**
 * A 404/501 reads as the backend not serving the endpoint yet. A 501 says so
 * outright and gets no Try Again; a 404 can also be a proxy or base-URL
 * problem for a route that is shipped, so it keeps the retry.
 */
export const NOT_SHIPPED_TEXT = englishT('achievements.errors.notShipped');

const TAB_PANEL_ID = 'achievements-panel';

/** The unlock date in the active locale ("Sep 15, 2026"), or null when the value is missing or unparseable. */
const formatDate = (date, format) => format.date(date, 'short') || null;

/**
 * The `lang` of a localized badge's title and description: `en` when they
 * are the backend's words (English, whatever the locale — see
 * `localizeAchievements`) on a page in another language, so assistive
 * technology reads them in the right voice (WCAG 3.1.2); otherwise nothing,
 * since they are in the page's language already.
 */
const copyLang = (achievement, locale) => (achievement.source === 'backend' && languageOf(locale) !== 'en' ? 'en' : undefined);

function AchievementCard({ achievement, isNew }) {
  const { t, locale, format } = useLocale();
  const Icon = ICONS[achievement.icon] ?? FaTrophy;
  const unlockedOn = achievement.unlocked ? formatDate(achievement.unlockedAt, format) : null;
  // An attribute has no language of its own, so the English of a backend
  // title in this label is not marked; the heading it names is.
  const progress = formatProgress(achievement, t, format);
  const progressLabel = t('achievements.badge.progressLabel', { title: achievement.title, progress });
  const lang = copyLang(achievement, locale);

  return (
    <li
      className={`${styles.badge} ${achievement.unlocked ? styles.badgeUnlocked : styles.badgeLocked}`}
      data-testid={`achievement-${achievement.key}`}
      data-unlocked={achievement.unlocked ? 'true' : 'false'}
      data-state={achievement.state}
      data-hidden={achievement.hidden ? 'true' : 'false'}
    >
      <div className={styles.medallion} aria-hidden="true">
        <Icon className={styles.medallionIcon} />
        {!achievement.unlocked && <FaLock className={styles.medallionLock} />}
      </div>
      <div className={styles.badgeBody}>
        <div className={styles.badgeHeading}>
          <h3 className={styles.badgeTitle} lang={lang}>{achievement.title}</h3>
          {isNew && <span className={styles.newChip}>{t('achievements.badge.new')}</span>}
          {/* Only an unlocked hidden badge is ever on the page (ENG-411), so
              the chip explains a card the listener has never seen before. */}
          {achievement.hidden && <span className={styles.secretChip}>{t('achievements.badge.secret')}</span>}
        </div>
        {achievement.description && <p className={styles.badgeDescription} lang={lang}>{achievement.description}</p>}
        {achievement.unlocked ? (
          <p className={styles.unlockedText}>
            <FaTrophy aria-hidden="true" />
            {unlockedOn ? t('achievements.badge.unlockedOn', { date: unlockedOn }) : t('achievements.badge.unlocked')}
          </p>
        ) : achievement.unavailable ? (
          <p className={styles.unavailableText}>{t('common.status.notAvailableYet')}</p>
        ) : (
          <div className={styles.progress}>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-label={progressLabel}
              aria-valuemin={0}
              aria-valuemax={achievement.target}
              aria-valuenow={achievement.progress}
              aria-valuetext={progress}
            >
              <span className={styles.progressFill} style={{ width: `${achievement.percent}%` }} />
            </div>
            <span className={styles.progressText}>{progress}</span>
          </div>
        )}
      </div>
    </li>
  );
}

function ListenerAchievementsPage() {
  const navigate = useNavigate();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const { t, dir, locale, format } = useLocale();

  const [filter, setFilter] = useState('all');
  // As normalized from the backend, with its English copy; `localized` below
  // is what renders.
  const [achievements, setAchievements] = useState([]);
  // The backend's overall status: `processing` means the served progress may
  // lag, `unavailable` that a source is not enabled on this deployment.
  const [status, setStatus] = useState(ACHIEVEMENTS_STATUS.READY);
  const [loading, setLoading] = useState(true);
  // `{ key, retryable }`: the message's catalog key; only a 501 (endpoint not implemented) gets no Try Again.
  const [error, setError] = useState(null);
  // Keys of badges unlocked since the listener last looked; fixed for the
  // visit so the "New" chips do not vanish the moment they are recorded as seen.
  const [newKeys, setNewKeys] = useState(() => new Set());
  // How many hidden badges the response carried that this page may not show
  // (ENG-411). Never rendered and never named: it only tells the page that
  // the catalogue it has is not the whole one, so nothing may claim the
  // listener has collected every badge there is.
  const [withheldCount, setWithheldCount] = useState(0);
  // The display groups, in order: the backend's served categories (ENG-826).
  const [categories, setCategories] = useState([]);

  // ProfilePage's body override: the theme's body colour would otherwise show
  // past the ink page on overscroll.
  useEffect(() => {
    document.body.classList.add('achievements-page-body');
    return () => document.body.classList.remove('achievements-page-body');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      let payload;
      try {
        payload = await fetchWrapperRef.current.get(`${config.apiBaseUrl}${ACHIEVEMENTS_ENDPOINT}`);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load listener achievements:', err);
        const notShipped = err && (err.status === 404 || err.status === 501);
        setError({
          key: notShipped ? 'achievements.errors.notShipped' : 'achievements.errors.loadFailed',
          retryable: !(err && err.status === 501),
        });
        setLoading(false);
        return;
      }
      if (cancelled) return;

      const response = normalizeAchievementsResponse(payload);
      const seen = readSeenAchievementKeys();
      const unlockedKeys = response.achievements
        .filter((achievement) => achievement.unlocked)
        .map((achievement) => achievement.key);

      setAchievements(response.achievements);
      setCategories(response.categories);
      setStatus(response.status);
      setWithheldCount(response.withheldCount);
      setNewKeys(pickNewAchievementKeys(response.achievements, seen));
      // Union, never overwrite: a badge missing from this response (a
      // catalogue change, say) must not read as "New" if it comes back. A
      // response with no badges at all (an empty catalogue, or a 2xx that was
      // not the endpoint's JSON) says nothing about what this device has
      // seen, so it must not start the seen set either: an empty set would
      // retire the 24h first-visit rule and the next good load would flag
      // every long-held badge as New.
      if (response.achievements.length > 0) {
        writeSeenAchievementKeys([...(seen ?? []), ...unlockedKeys]);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Badge titles and descriptions in the selected locale, from the backend's
  // stable keys (ENG-381); the backend's English where the client cannot
  // localize truthfully. Everything below reads this list, so the cards, the
  // progress-bar labels and "Next up" agree.
  const localized = useMemo(() => localizeAchievements(achievements, t), [achievements, t]);
  const summary = useMemo(() => summarizeAchievements(localized, withheldCount), [localized, withheldCount]);
  // The nudge for a listener with nothing yet, naming only what can still earn
  // a badge here; nothing when there is no activity but also nothing to earn.
  const intro = useMemo(
    () => (hasAchievementActivity(achievements) ? null : introText(earnableMetrics(achievements), t)),
    [achievements, t]
  );

  const visible = localized.filter((achievement) =>
    filter === 'all' ? true : filter === 'unlocked' ? achievement.unlocked : !achievement.unlocked
  );
  // The backend's categories in its order (headed in the locale where this
  // build knows the key, else with the backend's title), then "More badges"
  // for anything that fits none, so no badge it serves is left out.
  const grouped = groupAchievements(visible, categories, t);

  const overallPercent = summary.total > 0 ? Math.round((summary.unlockedCount / summary.total) * 100) : 0;

  // Arrow keys move between filter tabs and select as they go (WAI-ARIA tabs
  // with automatic activation); Home/End jump to the first/last tab. Under
  // `dir="rtl"` the tabs run right-to-left, so the arrows swap direction.
  const onTabKeyDown = (event, index) => {
    const forward = dir === 'rtl' ? -1 : 1;
    const step = { ArrowRight: forward, ArrowLeft: -forward, Home: -index, End: FILTERS.length - 1 - index }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = FILTERS[(index + step + FILTERS.length) % FILTERS.length];
    setFilter(next.id);
    document.getElementById(`tab-${next.id}`)?.focus();
  };

  const loaded = !loading && !error;

  // The summary card's lower half: the count, overall bar and nudges. Only
  // once the badges are in, so the card is just the title while they load.
  const renderSummary = () => (
    <>
      <section className={styles.summary} aria-label={t('achievements.summary.label')}>
        <span className={styles.eyebrow}>{t('achievements.summary.eyebrow')}</span>
        <div className={styles.summaryCount}>
          {richMessage(
            t('achievements.summary.badgesUnlocked', { count: summary.total }),
            { unlocked: <span className={styles.summaryNumber} data-testid="unlocked-count">{summary.unlockedCount}</span> },
            { text: (chunk) => <span className={styles.summaryOf}>{chunk}</span> }
          )}
        </div>
        <div
          className={styles.summaryTrack}
          role="progressbar"
          aria-label={t('achievements.summary.progressLabel')}
          aria-valuemin={0}
          aria-valuemax={summary.total}
          aria-valuenow={summary.unlockedCount}
        >
          <span className={styles.summaryFill} style={{ width: `${overallPercent}%` }} />
        </div>
        {summary.nextUp ? (
          <p className={styles.summaryNext}>
            {richMessage(t('achievements.summary.nextUp', { progress: formatProgress(summary.nextUp, t, format) }), {
              title: <strong lang={copyLang(summary.nextUp, locale)}>{summary.nextUp.title}</strong>,
            })}
          </p>
        ) : summary.allCollected ? (
          // Only when the set really is finished: a withheld secret badge
          // (ENG-411) means it is not, and the page says nothing rather than
          // congratulate a listener it will later un-congratulate.
          <p className={styles.summaryNext}>{t('achievements.summary.allCollected')}</p>
        ) : null}
      </section>

      {STATUS_KEYS[status] && (
        <p className={styles.statusNotice} role="status" data-testid="status-notice">
          {t(STATUS_KEYS[status])}
        </p>
      )}

      {intro && (
        <div className={styles.introBanner}>
          <p className={styles.emptyText}>{intro}</p>
          <button type="button" className={styles.btnPrimary} onClick={() => navigate('/performers')}>
            {t('common.actions.findPerformers')}
          </button>
        </div>
      )}
    </>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className={styles.stateContainer}>
          <LoadingSpinner />
        </div>
      );
    }
    if (error) {
      return (
        <div className={styles.stateContainer}>
          <p className={styles.errorText} role="alert">{t(error.key)}</p>
          {error.retryable && (
            <button type="button" className={styles.btnOutline} onClick={() => window.location.reload()}>
              {t('common.actions.tryAgain')}
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <div className={styles.tabs} role="tablist" aria-label={t('achievements.filters.label')}>
          {FILTERS.map((tab, index) => (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={filter === tab.id}
              aria-controls={TAB_PANEL_ID}
              tabIndex={filter === tab.id ? 0 : -1}
              className={`${styles.tab}${filter === tab.id ? ` ${styles.tabActive}` : ''}`}
              onClick={() => setFilter(tab.id)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <div id={TAB_PANEL_ID} role="tabpanel" aria-labelledby={`tab-${filter}`} tabIndex={0}>
          {grouped.length === 0 ? (
            <div className={styles.stateContainer}>
              <p className={styles.emptyText}>{t(emptyMessageKey(filter, summary))}</p>
              {filter === 'unlocked' && summary.total > 0 && (
                <button type="button" className={styles.btnPrimary} onClick={() => navigate('/performers')}>
                  {t('common.actions.findPerformers')}
                </button>
              )}
            </div>
          ) : (
            grouped.map((group) => (
              <section key={group.category} className={styles.group} aria-labelledby={`group-${group.category}`}>
                <h2
                  id={`group-${group.category}`}
                  className={styles.groupTitle}
                  lang={group.source === 'backend' && languageOf(locale) !== 'en' ? 'en' : undefined}
                >
                  {group.label}
                </h2>
                <ul className={styles.grid}>
                  {group.items.map((achievement) => (
                    <AchievementCard
                      key={achievement.key}
                      achievement={achievement}
                      isNew={newKeys.has(achievement.key)}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </>
    );
  };

  // ProfilePage's two-card layout: the flexible card on the left, the
  // fixed-width one on the right, stacking below 900px. The summary card is
  // first in the DOM (CSS `order` moves it right on a desktop) so the title
  // and a new listener's nudge lead on a phone rather than sit under every
  // badge.
  return (
    <div className={styles.page}>
      <StreamPageHeader />

      <div className={styles.main}>
        <div className={styles.summaryCard}>
          <h1 className={styles.title}>{t('achievements.title')}</h1>
          <p className={styles.subtitle}>
            {t('achievements.subtitle')}
          </p>
          {loaded && renderSummary()}
        </div>

        <div className={styles.badgesCard}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
}

export default ListenerAchievementsPage;
